/**
 * ConnectSteps — the canary-cli "install / authenticate / connect" guide.
 * Renders three numbered steps with copy-pasteable dark code blocks, plus
 * an expandable "More commands" disclosure. Shared between the Overview-tab
 * Connect card and the right-side Connect-locally drawer.
 */
import React from 'react';
import { Button, Text } from '@chatsdet/ui';
import { CaretDown, Check, Copy } from '@phosphor-icons/react';
import { BRAND } from '../../../brand';

function CodeBlock({
  command,
  disableCopy = false,
}: {
  command: string;
  /** Hide the copy button — used when the command embeds a literal
   *  `<instance-id>` placeholder that would be hostile to paste. */
  disableCopy?: boolean;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail when the document isn't focused; ignore.
    }
  }, [command]);

  return (
    <div className="group flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-slate-100 font-mono text-[12.5px]">
      <span aria-hidden className="text-emerald-400">$</span>
      <span className="flex-1 truncate">{command}</span>
      {!disableCopy && (
        <button
          type="button"
          onClick={handleCopy}
          className="shrink-0 rounded-sm p-1 text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
          aria-label="Copy command"
        >
          {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
        </button>
      )}
    </div>
  );
}

export interface ConnectStepsProps {
  /** Real instance ID when shown next to a running sandbox. Omit to render
   *  generic guidance with a `<instance-id>` placeholder (used in the
   *  empty-state hero where no sandbox exists yet). */
  instanceId?: string;
}

export function ConnectSteps({ instanceId }: ConnectStepsProps) {
  const { name, cliCommand: cli, cliPackage } = BRAND;
  const [showMore, setShowMore] = React.useState(false);
  const hasInstanceId = !!instanceId && instanceId.trim().length > 0;
  const idForCommand = hasInstanceId ? instanceId : '<instance-id>';

  return (
    <div className="flex flex-col gap-5">
      {/* Install */}
      <div className="flex flex-col gap-1.5">
        <Text weight="semibold">Install the {name} CLI</Text>
        <CodeBlock command={`npm install -g ${cliPackage}`} />
      </div>

      {/* Authenticate */}
      <div className="flex flex-col gap-1.5">
        <Text weight="semibold">Authenticate</Text>
        <Text size="sm" color="muted">
          Opens a device-code flow in your browser.
        </Text>
        <CodeBlock command={`${cli} login`} />
      </div>

      {/* Brief your AI agent */}
      <div className="flex flex-col gap-1.5">
        <Text weight="semibold">Brief your AI coding agent</Text>
        <Text size="sm" color="muted">
          Run this and paste the output into Claude Code, Cursor, or any AI
          coding agent as instructions. The guide teaches the agent how to drive
          sandboxes on your behalf.
        </Text>
        <CodeBlock command={`${cli} sandbox guide v1`} />
      </div>

      {/* More commands disclosure */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          onPress={() => setShowMore((v) => !v)}
          iconLeft={
            <CaretDown
              size={12}
              className={`transition-transform ${showMore ? 'rotate-180' : ''}`}
            />
          }
          className="px-2"
        >
          {showMore ? 'Hide commands' : 'More commands'}
        </Button>
        {showMore && (
          <div className="mt-3 flex flex-col gap-3">
            <div>
              <Text size="xs" color="muted" className="mb-1">
                Run a command on the sandbox
              </Text>
              <CodeBlock
                command={`${cli} sandbox run-command ${idForCommand} 'docker ps'`}
                disableCopy={!hasInstanceId}
              />
            </div>
            <div>
              <Text size="xs" color="muted" className="mb-1">
                Tail recent container logs
              </Text>
              <CodeBlock
                command={`${cli} sandbox logs ${idForCommand}`}
                disableCopy={!hasInstanceId}
              />
            </div>
            <div>
              <Text size="xs" color="muted" className="mb-1">
                Run an action
              </Text>
              <CodeBlock
                command={`${cli} sandbox action execute ${idForCommand} <slug>`}
                disableCopy={!hasInstanceId}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

ConnectSteps.displayName = 'ConnectSteps';
